import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CellCard, CellCommon } from '@root/components/Cell';
import Button from '@root/components/Button';
import { IDetailState } from '@root/hooks/subscription/useDetail';
import { DATE_FORMAT, EPayMode } from '@root/constant';
import SafeAreaInset from '@root/components/SafeAreaInset';
import { isInIosApp } from '@root/shared';
import './index.less';
import Icon from '@root/components/Icon';

const DetailContent = ({ data }: { data: IDetailState }) => {
  const { t } = useTranslation();
  const {
    detail,
    requesting,
    onSwitchRenewal,
    onAdjust,
    onApplePayCheckHandle,
  } = data;

  const {
    packageName,
    packageDesc,
    orderType,
    subScribeType,
    subScribeStatus,
    nextRenewal,
    packageType,
    price,
    isRenewal,
    isSupportRenew,
    devList,
    maxDevNum,
    orderStatus,
    whetherPay,
    payMode,
    isUnlimited,
  } = detail;

  const payModeText = useMemo(() => {
    return (
      {
        [EPayMode.PayPal]: ` / ${t('payPal')}`,
        [EPayMode.ApplePay]: ` / ${t('apple')}`,
        [EPayMode.CreditCard]: ` / ${t('credit_card')}`,
      }[payMode] || ''
    );
  }, [payMode, t]);

  const subScribeTypeText = useMemo(() => {
    let v = '';
    if (subScribeStatus === 0) {
      return t('no_subscript');
    }

    if (orderType === 'ruleGive' || orderType === 'giftCard') {
      return t('trial_service');
    }

    // 20220818 不知道有没有隐藏炸弹 不敢改这些判断 233
    // 套餐降级的时候是 -1 是属于非订阅状态
    // 但礼品卡兑换的套餐也会返回 -1 所以优先判断礼品卡的 -1
    if (subScribeStatus === -1) {
      return t('no_subscript');
    }

    if (subScribeType === 'month') {
      v = t('month_renewal');
    } else if (subScribeType === 'year') {
      v = t('year_renewal');
    } else if (subScribeType === 'day') {
      v = t('day_renewal');
    } else {
      v = t('no_subscript');
    }

    return v;
  }, [orderType, subScribeStatus, subScribeType, t]);

  const subscriptionStateText = useMemo(() => {
    if (subScribeStatus === 2) {
      return t('plan_canceling');
    }

    if (subScribeStatus === 3) {
      return t('plan_activation');
    }

    if (subScribeStatus <= 1) {
      const now = dayjs(Date.now()).format('YYYY-MM-DD');
      const nextDate = dayjs(now).diff(
        dayjs(nextRenewal).format('YYYY-MM-DD'),
        'days',
      );

      const next = -nextDate;

      // 不会存在过期的套餐
      // if (next < 0) {
      //   return t('package_expired', { days: -next });
      // }

      if (next < 1) {
        return `1 ${t('day_left')}`;
      }

      return `${next} ${t('day_left')}`;
    }

    return '';
  }, [nextRenewal, subScribeStatus, t]);

  const nextRenewalDate = useMemo(() => {
    return dayjs(nextRenewal).format(DATE_FORMAT);
  }, [nextRenewal]);

  const FooterBtn = useMemo(() => {
    /* 苹果套餐取消订阅 */
    if (payMode === EPayMode.ApplePay) {
      if (isInIosApp && subScribeStatus === 1) {
        return (
          <div className="flex-section-footer apple-pay-footer">
            <Button
              type="text"
              className="check-btn"
              onClick={onApplePayCheckHandle}
            >
              {t('Check_Subscription')}
              <Icon type="question" fontSize={20} />
            </Button>
            <SafeAreaInset />
          </div>
        );
      }
      return null;
    }

    // 存在退款行为 || 非付费套餐
    if ([7, 8, 9].includes(+orderStatus) || packageType !== 1) {
      return null;
    }

    //  需要续订时在苹果手机里面不展示 || 套餐不支持订阅 ||  不可订阅并且已订阅 || 订阅状态 > 1 不显示按钮
    const hideRenewal =
      (isInIosApp && !whetherPay) ||
      !isSupportRenew ||
      (subScribeStatus === -1 && whetherPay) ||
      subScribeStatus > 1;

    // 目前隐藏升降级入口 所以不需要按钮时直接返回空
    if (hideRenewal) {
      return null;
    }

    const offRenewal = subScribeStatus === 1 && whetherPay;

    return (
      <div className="flex-section-footer">
        {/* 隐藏升降级入口，因为无限套餐暂不支持 */}
        {/* {changePlanFlag === 1 && (
              <Button onClick={onChangePackage}>{t('change_plan')}</Button>
            )} */}
        <Button
          // color={changePlanFlag === 1 || offRenewal ? 'surface' : 'primary'}
          color={offRenewal ? 'surface' : 'primary'}
          onClick={onSwitchRenewal}
          loading={requesting}
        >
          {offRenewal ? t('off_renewal') : t('open_renewal')}
        </Button>
        <SafeAreaInset />
      </div>
    );
  }, [
    isSupportRenew,
    onApplePayCheckHandle,
    onSwitchRenewal,
    orderStatus,
    packageType,
    payMode,
    requesting,
    subScribeStatus,
    t,
    whetherPay,
  ]);

  return (
    <>
      <div className="flex-section-auto subscription-detail">
        <CellCard>
          <div className="detail-title">
            <h2>{packageName}</h2>
            <p
              className="desc"
              dangerouslySetInnerHTML={{
                __html: packageDesc && packageDesc.replace(/#/gm, '<br>'),
              }}
            />
            {isUnlimited ? (
              <p className="desc">{t('unlimited_devices')}</p>
            ) : (
              <p className="desc">{`for ${maxDevNum} ${
                +maxDevNum > 1 ? 'Devices' : 'Device'
              }`}</p>
            )}
          </div>
          {/* 0元套餐不展示 */}
          {packageType === 1 && (
            <>
              <CellCommon
                inner
                title={t('plan_detail_renewal_setting')}
                description={`${subScribeTypeText}${payModeText}`}
              >
                {subscriptionStateText}
              </CellCommon>
              {isRenewal ? (
                <CellCommon
                  inner
                  title={t('plan_detail_next_renewal')}
                  description={nextRenewalDate}
                >
                  {price ? `$${price}` : 0}
                </CellCommon>
              ) : null}
            </>
          )}
        </CellCard>

        {/* 默认或者免费套餐 */}
        {packageType !== 1 && (
          <p className="basic-package">
            {t('free_tip', { packageName, maxDevNum })}
          </p>
        )}

        <div className="detail-card">
          <div className="detail-title-flex">
            <h2>{t('plan_detail_device_under_plan')}</h2>
            {/* 20220812 {detailData.deviceId && ()} 实际上没有返回这个deviceId字段 先不加*/}
            <Button size="auto" onClick={onAdjust}>
              {t('adjust')}
            </Button>
          </div>
          {devList && devList.length ? (
            <ul>
              {devList.map((i) => (
                <li key={i.deviceId}>{i.deviceName}</li>
              ))}
            </ul>
          ) : (
            <p className="unbind">{t('no_data')}</p>
          )}
        </div>
        <SafeAreaInset />
      </div>
      {FooterBtn}
    </>
  );
};

export default DetailContent;
